import * as React from 'react';
import { parse } from 'date-fns';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { FlightPointFragment, TemperatureUnits } from '@websky/graphql';
import { useTheme } from '../../theme';
import { getTemperaturePrefix } from '../../Weather/utils';
import { DepartureIcon, ArrivalIcon, DetailsIcon } from './Icons';
import { weatherIcons } from '../../Weather/icons';
import { format } from '../../utils';

export interface Props extends FlightPointFragment {
	type: 'departure' | 'arrival';
	onDetailsClick?: (airportId: string) => void;
	className?: string;
}

const Location: React.FC<Props> = ({ type, airport, date, time, terminal, weather, onDetailsClick, className }) => {
	const { Location: css } = useTheme('Location');
	const { t } = useTranslation('Location');
	const flightDate = parse(date, 'yyyy-MM-dd', new Date());
	const weatherIcon = weather?.type && weatherIcons[weather.type];
	const directionIcon = type === 'departure' ? DepartureIcon : ArrivalIcon;
	const image = airport.images?.panorama?.url;

	return (
		<div className={cn(css.wrapper, className)}>
			<div className={css.header}>
				<div className={css.airport}>
					{image && <div className={css.direction}>{directionIcon}</div>}
					<div className={css.airport__iata}>{airport.iata}</div>
					<div className={css.airport__name}>
						{airport.city?.name},<br />
						{airport.title}
					</div>
				</div>

				<div className={css.bottom}>
					<span className={css.when}>
						<span>{time},</span>
						<span className={css.date}>{format(flightDate, 'dd MMM')}</span>
					</span>

					{terminal && (
						<span className={css.terminal}>
							{t('Terminal')} <span className={css.terminal__code}>{terminal}</span>
						</span>
					)}
				</div>
			</div>

			<div className={css.footer} style={image ? { backgroundImage: `url(${image})` } : {}}>
				{!image && <div className={css.footer__direction}>{directionIcon}</div>}

				{weather && (
					<div className={css.weather}>
						{weatherIcon}

						<span className={css.weather__value}>
							{getTemperaturePrefix(weather.temperature)}
							{Math.abs(Math.round(weather.temperature))}&deg;
							{weather.measurement === TemperatureUnits.Fahrenheit && 'F'}
						</span>
					</div>
				)}

				{onDetailsClick && (
					<div className={css.details} onClick={() => onDetailsClick(airport.id)}>
						{t('Details')} {DetailsIcon}
					</div>
				)}
			</div>
		</div>
	);
};

export default Location;
